import {useCallback, useRef} from 'react';
import {Bet} from '../../generated/smg_service_pb';

type FullSwingSourceSnapshot = { backend: string, activeBetId?: string, amountMinor: number };

type FullSwingRefreshSnapshot = {
  sourceKey: string, sourceGeneration: number, targetBetId?: string, cancellationGeneration: number,
  requestGeneration: number
};

type FullSwingRefreshSnapshotArgs = {
  source: FullSwingSourceSnapshot, activeBet?: Bet, cancellationGenerations: Map<string, number>,
  sourceGeneration: number, requestGeneration: number
};

type UseFullSwingAcceptedBetRefreshProps = {
  backend: string, activeBetId?: string | number | bigint, activeBetAmountMinor: number, activeBet?: Bet,
  fetchAcceptedBets: () => Promise<Bet[]>, onBetsFound: (bets: Bet[]) => void, onEmpty: () => void,
  onError: (error: unknown) => void
};

const normalizeBetId = (value: string | number | bigint | undefined): string | undefined => {
  const normalized = value === undefined ? '' : String(value);
  return normalized && normalized !== '0' ? normalized : undefined;
};

const getFullSwingSourceKey = (source: FullSwingSourceSnapshot): string => {
  if (source.backend !== 'bay-session') return source.backend;
  return [source.backend, source.activeBetId ?? '', source.amountMinor].join(':');
};

const createFullSwingRefreshSnapshot = (args: FullSwingRefreshSnapshotArgs): FullSwingRefreshSnapshot => {
  const {source, activeBet, cancellationGenerations, sourceGeneration, requestGeneration} = args;
  const localActiveBetId = normalizeBetId(activeBet?.betId);
  const targetBetId = source.backend === 'bay-session' ? source.activeBetId ?? localActiveBetId : localActiveBetId;

  return {
    sourceKey: getFullSwingSourceKey(source), targetBetId, sourceGeneration, requestGeneration,
    cancellationGeneration: targetBetId ? cancellationGenerations.get(targetBetId) ?? 0 : 0
  };
};

const matchesFullSwingRefreshSnapshot = (snapshot: FullSwingRefreshSnapshot, currentSource: FullSwingSourceSnapshot, currentSourceGeneration: number, cancellationGenerations: Map<string, number>): boolean => {
  if (snapshot.sourceGeneration !== currentSourceGeneration) return false;
  if (getFullSwingSourceKey(currentSource) !== snapshot.sourceKey) return false;
  if (!snapshot.targetBetId) return true;
  return (cancellationGenerations.get(snapshot.targetBetId) ?? 0) === snapshot.cancellationGeneration;
};

const getCurrentFullSwingBets = (bets: Bet[], source: FullSwingSourceSnapshot, cancellationGenerations: Map<string, number>): Bet[] => {
  const nonCancelledBets = bets.filter(bet => {
    const betId = normalizeBetId(bet.betId);
    return !betId || !cancellationGenerations.has(betId);
  });

  if (source.backend !== 'bay-session' || !source.activeBetId) return nonCancelledBets;
  return nonCancelledBets.filter(bet => normalizeBetId(bet.betId) === source.activeBetId);
};

const advanceFullSwingCancellationGeneration = (cancellationGenerations: Map<string, number>, betId: bigint): void => {
  const cancellationId = normalizeBetId(betId);
  if (!cancellationId) return;

  const currentGeneration = cancellationGenerations.get(cancellationId) ?? 0;
  cancellationGenerations.set(cancellationId, currentGeneration + 1);
};

const useFullSwingAcceptedBetRefresh = (props: UseFullSwingAcceptedBetRefreshProps) => {
  const {
    backend, activeBetId, activeBetAmountMinor, activeBet, fetchAcceptedBets, onBetsFound, onEmpty, onError
  } = props;
  const currentSource: FullSwingSourceSnapshot = {
    backend, activeBetId: normalizeBetId(activeBetId), amountMinor: activeBetAmountMinor
  };
  const currentSourceKey = getFullSwingSourceKey(currentSource);
  const callbacksRef = useRef({fetchAcceptedBets, onBetsFound, onEmpty, onError});
  const activeBetRef = useRef<Bet>();
  const sourceSnapshotRef = useRef<FullSwingSourceSnapshot>(currentSource);
  const sourceGenerationRef = useRef(0);
  const previousSourceKeyRef = useRef(currentSourceKey);
  const cancellationGenerationsRef = useRef(new Map<string, number>());
  const refreshRequestGenerationRef = useRef(0);
  const refreshFenceRef = useRef(0);
  const targetlessRefreshFenceRef = useRef(0);
  const lastAppliedRequestGenerationRef = useRef(0);
  const lastCancellationRef = useRef<{betId: string, at: number} | undefined>();

  callbacksRef.current = {fetchAcceptedBets, onBetsFound, onEmpty, onError};

  if (previousSourceKeyRef.current !== currentSourceKey) {
    previousSourceKeyRef.current = currentSourceKey;
    sourceGenerationRef.current += 1;
  }

  // Keep refs synchronized during render so promise callbacks compare against
  // the latest source and active bet, not the render that started the request.
  activeBetRef.current = activeBet;
  sourceSnapshotRef.current = currentSource;

  const isCurrentRefresh = useCallback((snapshot: FullSwingRefreshSnapshot): boolean => {
    if (snapshot.requestGeneration < refreshFenceRef.current) return false;
    if (!snapshot.targetBetId && snapshot.requestGeneration < targetlessRefreshFenceRef.current) return false;
    if (snapshot.requestGeneration < lastAppliedRequestGenerationRef.current) return false;

    return matchesFullSwingRefreshSnapshot(snapshot, sourceSnapshotRef.current, sourceGenerationRef.current, cancellationGenerationsRef.current);
  }, []);

  const refreshAcceptedBet = useCallback((): void => {
    const requestGeneration = refreshRequestGenerationRef.current + 1;
    refreshRequestGenerationRef.current = requestGeneration;
    const snapshot = createFullSwingRefreshSnapshot({
      source: sourceSnapshotRef.current, activeBet: activeBetRef.current, sourceGeneration: sourceGenerationRef.current,
      cancellationGenerations: cancellationGenerationsRef.current, requestGeneration
    });

    callbacksRef.current.fetchAcceptedBets().then(res => {
      if (!isCurrentRefresh(snapshot)) return;

      const currentBets = getCurrentFullSwingBets(res, sourceSnapshotRef.current, cancellationGenerationsRef.current);
      if (currentBets.length) {
        lastAppliedRequestGenerationRef.current = Math.max(lastAppliedRequestGenerationRef.current, snapshot.requestGeneration);
        callbacksRef.current.onBetsFound(currentBets);
        return;
      }

      // A non-empty response that contains only an older bet is stale when
      // Bay already identifies another active bet. It must not clear or
      // replace local state while the current bet is being re-fetched.
      const currentSource = sourceSnapshotRef.current;
      const hasAuthoritativeBayBet = currentSource.backend === 'bay-session' && Boolean(currentSource.activeBetId);
      if (res.length && hasAuthoritativeBayBet) return;

      lastAppliedRequestGenerationRef.current = Math.max(lastAppliedRequestGenerationRef.current, snapshot.requestGeneration);
      callbacksRef.current.onEmpty();

    }).catch(error => {
      if (!isCurrentRefresh(snapshot)) return;

      callbacksRef.current.onError(error);
    });
  }, [isCurrentRefresh]);

  const recordCancellation = useCallback((betId: bigint): void => {
    advanceFullSwingCancellationGeneration(cancellationGenerationsRef.current, betId);
    const cancellationId = normalizeBetId(betId);
    if (cancellationId) lastCancellationRef.current = {betId: cancellationId, at: Date.now()};
    targetlessRefreshFenceRef.current = Math.max(targetlessRefreshFenceRef.current, refreshRequestGenerationRef.current + 1);
  }, []);

  const invalidateRefreshes = useCallback((): void => {
    refreshFenceRef.current = Math.max(refreshFenceRef.current, refreshRequestGenerationRef.current + 1);
  }, []);

  const getCurrentActiveBetId = useCallback((): string | undefined => sourceSnapshotRef.current.activeBetId, []);

  // SMG-4201: a bet this screen cancelled. The stream can keep announcing its id for a while (the frame that clears it
  // is best effort), and the open bet that replaces it may be read back together with it.
  const isCancelled = useCallback((betId: string | number | bigint | undefined): boolean => {
    const normalizedBetId = normalizeBetId(betId);

    return normalizedBetId !== undefined && cancellationGenerationsRef.current.has(normalizedBetId);
  }, []);

  // The stream's active bet id, unless it is the id of a bet this screen has already cancelled.
  const getLiveActiveBetId = useCallback((): string | undefined => {
    const activeBetId = sourceSnapshotRef.current.activeBetId;

    return activeBetId !== undefined && !cancellationGenerationsRef.current.has(activeBetId) ? activeBetId : undefined;
  }, []);

  // The latest bet this screen cancelled and when, for diagnostics.
  const getLastCancellation = useCallback(() => lastCancellationRef.current, []);

  return {
    refreshAcceptedBet, recordCancellation, invalidateRefreshes, getCurrentActiveBetId, getLiveActiveBetId, isCancelled,
    getLastCancellation
  };
};

export default useFullSwingAcceptedBetRefresh;
